import React, { useState } from 'react';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { Alert, Field } from '../../components/ui.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';
import PageHero from '../../components/PageHero.jsx';
import Icon from '../../components/Icon.jsx';
import { useContent } from '../../content.jsx';
import { ContactDetails } from '../../components/Footer.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

export const CONTACT_EMAIL = 'info@pmecanada.com';
const blank = { name: '', email: '', phone: '', subject: '', message: '', website: '' };

export default function Contact() {
  const { t, lang, errorText } = useI18n();
  usePageTitle('nav.contact');
  const [form, setForm] = useState(blank);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (form.message.trim().length < 10) return setError(t('errors.message_too_short'));
    if (form.phone && form.phone.replace(/\D/g, '').length !== 10) return setError(t('errors.invalid_phone'));
    setBusy(true);
    try {
      await api('/contact', { method: 'POST', body: { ...form, lang } });
      setSent(true);
      setForm(blank);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHero title={t('contact.title')} lead={t('contact.lead')}><TutorialSlot place="site.contact" /></PageHero>
      <section className="section">
        <div className="container contact-layout">
          <div className="card">
            {sent ? (
              <div className="form">
                <Alert kind="success">{t('contact.sent')}</Alert>
                <div><button type="button" className="btn btn-ghost" onClick={() => setSent(false)}>{t('contact.sendAnother')}</button></div>
              </div>
            ) : (
              <form className="form" onSubmit={submit}>
                <div className="grid-form">
                  <Field label={t('contact.name')} htmlFor="c-name">
                    <input id="c-name" value={form.name} onChange={set('name')} autoComplete="name" required maxLength={120} />
                  </Field>
                  <Field label={t('contact.email')} htmlFor="c-email">
                    <input id="c-email" type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
                  </Field>
                </div>
                <div className="grid-form">
                  <Field label={t('contact.phone')} optional htmlFor="c-phone">
                    <PhoneInput id="c-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
                  </Field>
                  <Field label={t('contact.subject')} optional htmlFor="c-subject">
                    <input id="c-subject" value={form.subject} onChange={set('subject')} maxLength={200} />
                  </Field>
                </div>
                <Field label={t('contact.message')} hint={t('contact.messageHint')} htmlFor="c-message">
                  <textarea id="c-message" rows={7} value={form.message} onChange={set('message')} required maxLength={5000} />
                </Field>
                {/* Honeypot — hidden from people, filled in by bots. */}
                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="c-website">{t('contact.website')}</label>
                  <input id="c-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
                </div>
                <p className="field-hint">{t('contact.privacyNote')}</p>
                <Alert>{error}</Alert>
                <div>
                  <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
                    {busy ? t('common.sending') : t('contact.submit')}
                  </button>
                </div>
              </form>
            )}
          </div>

          <aside className="card contact-aside">
            <span className="feature-icon"><Icon name="mail" size={22} /></span>
            <h2>{t('contact.asideTitle')}</h2>
            <p className="muted">{t('contact.asideText')}</p>
            <ContactDetails large />
          </aside>
        </div>
      </section>
    </>
  );
}
